import { Component, Optional, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { InboxService, ParticipantService, SubjectParticipant } from '@galaxy/conversation/core';
import { TranslateService } from '@ngx-translate/core';
import type { SalesContact } from '@vendasta/protos/atlas/v1/types';
import { ConversationChannel } from '@vendasta/conversation';
import { GlobalParticipantType, Participant } from '@vendasta/protos/conversation/v1';
import { CountryCode, format } from 'libphonenumber-js';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { BusinessNavConfigService } from '../../config.service';
import { BusinessNavDataService } from '../../data.service';

@Component({
  selector: 'bc-sales-info-modal',
  templateUrl: './sales-info.modal.component.html',
  styleUrls: ['./sales-info.modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SalesInfoModalComponent {
  dialogRef = inject(MatDialogRef<SalesInfoModalComponent>);

  public headerText$: Observable<string>;
  public partnerId$: Observable<string>;
  public accountGroupId$: Observable<string>;
  public canAccessInbox$: Observable<boolean> = this.dataService.canAccessInbox$;

  subjectParticipants$ = combineLatest([this.configService.partnerId$, this.configService.accountGroupId$]).pipe(
    map(([partnerId, accountGroupId]) => [
      {
        internalParticipantId: accountGroupId,
        participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
      } as SubjectParticipant,
      {
        internalParticipantId: partnerId,
        participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
      } as SubjectParticipant,
    ]),
  );

  currentIAMParticipant$: Observable<Participant>;
  readonly conversationChannel = ConversationChannel.CONVERSATION_CHANNEL_INTERNAL;

  salespersonContact$: Observable<SalesContact> = this.dataService.salespersonContact$;
  salespersonPicture$: Observable<string> = this.dataService.salespersonPictureUrl$;
  contactUsModalDescription$: Observable<string> = this.dataService.contactUsModalDescription$;
  hideSalespersonContact$: Observable<boolean> = this.dataService.hideSalespersonContact$;
  showSupportCenterLink$: Observable<boolean> = this.dataService.showSupportCenterLink$;
  supportCenterLinkUrl$: Observable<string> = this.dataService.supportCenterLinkUrl$;
  supportCenterLinkLabel$: Observable<string> = this.dataService.supportCenterLinkLabel$;

  showSalespersonContact$: Observable<boolean> = combineLatest([
    this.dataService.hideSalespersonContact$,
    // salespersonContact$ only emits once a contact exists; seed null so combineLatest can resolve
    // when there is no salesperson (e.g. the server redacts it when hideSalespersonContact is set).
    this.dataService.salespersonContact$.pipe(startWith(null)),
  ]).pipe(map(([hide, contact]) => hide === false && !!contact));

  showSendMessageCard$: Observable<boolean> = combineLatest([
    this.dataService.canAccessInbox$,
    this.showSalespersonContact$,
  ]).pipe(map(([canAccessInbox, salespersonShown]) => canAccessInbox && !salespersonShown));

  constructor(
    private dataService: BusinessNavDataService,
    private t: TranslateService,
    private configService: BusinessNavConfigService,
    @Optional() private inboxService: InboxService,
    @Optional() private participantService: ParticipantService,
  ) {
    this.headerText$ = this.dataService.partnerName$.pipe(
      switchMap((partnerName) => {
        return this.t.stream('COMMON.CONTACT_SALESPERSON', { salesPersonName: partnerName });
      }),
    );
    this.initInboxAccess();
  }

  initInboxAccess(): void {
    if (!this.inboxService || !this.participantService) {
      return;
    }

    this.currentIAMParticipant$ = this.participantService?.buildIAMUserParticipant() as Observable<Participant>;
  }

  formatPhoneNumber(phoneNumber: string | number, countryCode?: string): string {
    if (!phoneNumber) {
      return '';
    }

    const numberString = phoneNumber.toString();
    if (numberString === '') {
      return '';
    }
    const formatting = 'NATIONAL';
    if (countryCode as CountryCode) {
      const newCountryCode = countryCode as CountryCode;
      return format(numberString, newCountryCode, formatting);
    }
    return numberString;
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
